import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Card, Row, Col, InputNumber, message, Spin } from 'antd'
import axios from 'util/Api'
import { getErrorMessage } from 'util/methods'

const IotIgnitionSetting = ({ match, form, assetData }) => {
  const { getFieldDecorator, setFieldsValue } = form
  const asset = match && match.params && match.params.asset

  // Form state management
  const [formState, setFormState] = useState({
    ignition: 1,
    movementAccelerometerVariance: null,
    batteryVoltage: null,
    delay_seconds: 0
  })

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  // Fetch IoT ignition setting data from API
  const fetchIotIgnitionSetting = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/iot-ignition-setting/asset/${asset}`)
      setData(response.data)
      // Update form state with fetched data
      const { ignition, movementAccelerometerVariance, batteryVoltage, delay_seconds } = response.data || {}
      const newFormState = {
        ignition: response.data ? ignition : 1,
        movementAccelerometerVariance: response.data ? movementAccelerometerVariance : null,
        batteryVoltage: response.data ? batteryVoltage : null,
        delay_seconds: response.data ? delay_seconds : 0
      }

      setFormState(newFormState)

      // Populate form fields
      setFieldsValue(newFormState)
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No existing settings found, use defaults
        console.log('No existing IoT ignition settings found, using defaults')
        setFieldsValue(formState)
      } else {
        console.error('Error fetching IoT ignition setting:', error)
        message.error('Failed to load IoT ignition settings. Using default values.')
        setFieldsValue(formState)
      }
    } finally {
      setLoading(false)
    }
  }

  // Initialize form on component mount
  useEffect(() => {
    if (asset) {
      fetchIotIgnitionSetting()
    }
  }, [asset])

  // Update form when formState changes
  useEffect(() => {
    if (!loading) {
      setFieldsValue(formState)
    }
  }, [formState, loading])

  const onFinish = async (values) => {
    try {
      // Prepare final data
      const formData = {
        ...values,
        asset
      }

      // Make API call to save the data
      const response = await axios.post('/iot-ignition-setting', formData)

      message.success('IoT Ignition Settings saved successfully!')

      // Refresh the form data from server
      fetchIotIgnitionSetting()
    } catch (error) {
      console.error('Error saving IoT ignition settings:', error)
      const errorMessage = getErrorMessage(error)
      console.log('errorMessage', errorMessage)
    }
  }

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo)
    console.log('Error fields:', errorInfo.errorFields)
    console.log('Values:', errorInfo.values)
    message.error('Please check the form for errors')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    form.validateFields((err, values) => {
      if (!err) {
        onFinish(values)
      } else {
        onFinishFailed({ errorFields: err, values })
      }
    })
  }

  // Update formState when form values change
  const handleFieldChange = (fieldName, value) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  return (
    <div style={{ padding: '24px' }}>
      <h3>{`${data ? 'Update' : 'Create'} Device Ignition Setting (${assetData && assetData.assetCode || '-'})`}</h3>
      <Form
        layout="vertical"
        onSubmit={handleSubmit}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin tip="Loading IoT ignition settings..." size="large" />
          </div>
        ) : (
          <>
            {/* Ignition Field */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Ignition">
                  {getFieldDecorator('ignition', {
                    initialValue: formState.ignition,
                    rules: [
                      { required: true, message: 'Ignition value is required' },
                      { type: 'number', min: 1, max: 1, message: 'Ignition must be a positive number' }
                    ]
                  })(
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter ignition value"
                      min={1}
                      max={1}
                      onChange={(value) => handleFieldChange('ignition', value)}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Movement Accelerometer Variance Field */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Movement Accelerometer Variance">
                  {getFieldDecorator('movementAccelerometerVariance', {
                    initialValue: formState.movementAccelerometerVariance,
                    rules: [
                      { type: 'number', message: 'Movement accelerometer variance must be a number' }
                    ]
                  })(
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter movement accelerometer variance"
                      onChange={(value) => handleFieldChange('movementAccelerometerVariance', value)}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Battery Voltage Field */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Battery Voltage">
                  {getFieldDecorator('batteryVoltage', {
                    initialValue: formState.batteryVoltage,
                    rules: [
                      { type: 'number', message: 'Battery voltage must be a number' }
                    ]
                  })(
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter battery voltage"
                      onChange={(value) => handleFieldChange('batteryVoltage', value)}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Delay Ignition Change Field */}
            <Form.Item style={{ marginTop: '24px' }}>
              <Card
                size="small"
                style={{
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #d0d7de',
                  marginBottom: '16px'
                }}
              >
                <div style={{ fontSize: '14px', color: '#24292f' }}>
                  <strong>Delay Ignition change by:</strong>
                  <div style={{ marginTop: '16px' }}>
                    <Form.Item label="Delay">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {getFieldDecorator('delay_seconds', {
                          initialValue: formState.delay_seconds,
                          rules: [
                            { required: false, message: 'Delay seconds is not required' },
                            { type: 'number', min: 0, message: 'Delay must be a non-negative number' }
                          ]
                        })(
                          <InputNumber
                            style={{ width: '200px' }}
                            placeholder="Enter delay (optional)"
                            min={0}
                            onChange={(value) => handleFieldChange('delay_seconds', value)}
                          />
                        )}
                        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#24292f' }}>seconds</span>
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </Card>
            </Form.Item>

            {/* AND Logic Visualization */}
            <Form.Item style={{ marginTop: '24px' }}>
              <Card
                size="small"
                style={{
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #d0d7de',
                  marginBottom: '16px'
                }}
              >
                <div style={{ fontSize: '14px', color: '#24292f' }}>
                  <strong>Asset will be considered as ON when:</strong>
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #d0d7de',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }}>
                    {(() => {
                      const conditions = []

                      // Add ignition condition if it has a value
                      if (formState.ignition !== null && formState.ignition !== undefined) {
                        conditions.push(`ignition = ${formState.ignition}`)
                      }

                      // Add movement accelerometer variance condition if it has a value
                      if (formState.movementAccelerometerVariance !== null && formState.movementAccelerometerVariance !== undefined) {
                        conditions.push(`movement_accelerometer_variance > ${formState.movementAccelerometerVariance}`)
                      }

                      // Add battery voltage condition if it has a value
                      if (formState.batteryVoltage !== null && formState.batteryVoltage !== undefined) {
                        conditions.push(`battery_voltage > ${formState.batteryVoltage}`)
                      }

                      if (conditions.length === 0) {
                        return <span style={{ color: '#656d76', fontStyle: 'italic' }}>No conditions set</span>
                      }

                      return conditions.join(' AND ')
                    })()}
                  </div>
                </div>
              </Card>
            </Form.Item>

            {/* Submit Button */}
            <Form.Item style={{ marginTop: '24px' }}>
              <Button type={data ? 'danger' : 'primary'} htmlType="submit" size="large" style={{ width: '100%' }}>
                {data ? 'Update' : 'Save'} Device Ignition Setting
              </Button>
            </Form.Item>
          </>
        )}
      </Form>
    </div>
  )
}

// Export with Form.create() HOC for Ant Design v3
export default Form.create()(IotIgnitionSetting)

